import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { normalizedToLevel, scoreToRiskLevel, getThreeLevelScale } from '@safetyhub/shared';
import { EditableRiskBadge } from './EditableRiskBadge';
import { ModifiedFieldHighlight, findModification } from './ModifiedFieldHighlight';
import type { ModifiedFieldInfo } from '../../../lib/inspections';

export interface RiskAssessmentData {
  likelihood: number;
  likelihoodLabel?: string;
  severity: number;
  severityLabel?: string;
  riskLevel: number;
  riskLevelLabel?: string;
  action?: string;
  riskColor?: string;
}

interface RiskAssessmentEditorProps {
  riskAssessment: RiskAssessmentData;
  config?: RiskAssessmentConfig | null;
  canEdit: boolean;
  /** 변경된 riskAssessment 전체 객체를 저장 */
  onSave: (updated: Partial<RiskAssessmentData>) => Promise<void>;
  /** read-only 표시 시 위험도 색상 결정용 */
  getRiskStyle?: (riskLevel: number, color?: string) => string;
  /** AI 수정 추적 정보 */
  aiModifications?: { modifiedFields?: ModifiedFieldInfo[] };
  /** 개별 필드 되돌리기 콜백 */
  onRevert?: (field: 'likelihood' | 'severity' | 'riskLevel', originalScore: number) => Promise<void>;
}

function makeOriginalFormatter(criteria: { level: number; label: string }[] | undefined) {
  return (value: unknown): string => {
    let score: number;
    if (typeof value === 'object' && value !== null && 'value' in value) {
      score = (value as { value: number }).value;
    } else if (typeof value === 'number') {
      score = value;
    } else {
      return String(value);
    }
    if (criteria && criteria.length > 0) {
      return normalizedToLevel(score, criteria)?.label ?? String(score);
    }
    return String(score);
  };
}

function defaultRiskStyle(riskLevel: number, color?: string): string {
  if (color) {
    const c = color.toUpperCase();
    if (c === '#EF4444' || c === '#DC2626' || c === '#B91C1C') return 'bg-red-100 text-red-800 border-red-200';
    if (c === '#F97316' || c === '#EA580C') return 'bg-orange-100 text-orange-800 border-orange-200';
    if (c === '#EAB308' || c === '#CA8A04' || c === '#F59E0B' || c === '#D97706') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (c === '#22C55E' || c === '#16A34A' || c === '#15803D') return 'bg-green-100 text-green-800 border-green-200';
  }
  if (riskLevel >= 64) return 'bg-red-100 text-red-800 border-red-200';
  if (riskLevel >= 36) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (riskLevel >= 16) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-green-100 text-green-800 border-green-200';
}

/**
 * 위험성 평가 편집 공통 컴포넌트.
 *
 * THREE_LEVEL config: 빈도/강도 입력 숨김 + 위험도 직접 편집
 * FREQUENCY_SEVERITY config: 빈도/강도 편집 + 위험도 read-only 산출
 */
export function RiskAssessmentEditor({
  riskAssessment,
  config,
  canEdit,
  onSave,
  getRiskStyle = defaultRiskStyle,
  aiModifications,
  onRevert,
}: RiskAssessmentEditorProps) {
  const isThreeLevel = config?.methodType === 'THREE_LEVEL';

  if (isThreeLevel) {
    // KOSHA 3단계 판단법 — 위험도 직접 편집, 빈도/강도 숨김
    const riskResult = config ? scoreToRiskLevel(
      riskAssessment.likelihood,
      riskAssessment.severity,
      config,
      riskAssessment.riskLevel,
    ) : null;
    const riskLabel = riskResult?.riskLabel ?? String(riskAssessment.riskLevel);

    if (!canEdit) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-medium text-xs ${getRiskStyle(riskAssessment.riskLevel, riskAssessment.riskColor)}`}>
          <span>위험도:</span>
          <span>{riskLabel}</span>
        </span>
      );
    }

    return (
      <ModifiedFieldHighlight
        modification={findModification(aiModifications?.modifiedFields, 'riskAssessment.riskLevel')}
        editable={!!canEdit}
        formatOriginal={makeOriginalFormatter(config ? getThreeLevelScale(config) : undefined)}
        originalPosition="tooltip"
        onRevert={onRevert ? async () => {
          const mod = findModification(aiModifications?.modifiedFields, 'riskAssessment.riskLevel');
          if (mod) {
            const originalObj = mod.originalValue as { value: number; label?: string } | number;
            const original = typeof originalObj === 'number' ? originalObj : originalObj.value;
            await onRevert('riskLevel', original);
          }
        } : undefined}
      >
        <EditableRiskBadge
          type="riskLevel"
          value={riskAssessment.riskLevel}
          label={riskLabel}
          config={config}
          onSave={async (newScore) => {
            await onSave({ riskLevel: newScore });
          }}
        />
      </ModifiedFieldHighlight>
    );
  }

  // FREQUENCY_SEVERITY — 빈도/강도 편집 + 위험도 read-only
  const riskResult = config
    ? scoreToRiskLevel(riskAssessment.likelihood, riskAssessment.severity, config)
    : null;
  const computedRiskLevel = riskResult?.riskLevel ?? riskAssessment.riskLevel;
  const computedRiskLabel = riskResult?.riskLabel ?? String(riskAssessment.riskLevel);
  const computedColor = riskResult?.color ?? riskAssessment.riskColor;

  return (
    <>
      {canEdit ? (
        <>
          <ModifiedFieldHighlight
            modification={findModification(aiModifications?.modifiedFields, 'riskAssessment.likelihood')}
            editable={!!canEdit}
            formatOriginal={makeOriginalFormatter(config?.likelihoodCriteria)}
            originalPosition="tooltip"
            onRevert={onRevert ? async () => {
              const mod = findModification(aiModifications?.modifiedFields, 'riskAssessment.likelihood');
              if (mod) {
                const originalObj = mod.originalValue as { value: number; label?: string } | number;
                const original = typeof originalObj === 'number' ? originalObj : originalObj.value;
                await onRevert('likelihood', original);
              }
            } : undefined}
          >
            <EditableRiskBadge
              type="likelihood"
              value={riskAssessment.likelihood}
              label={config ? (normalizedToLevel(riskAssessment.likelihood, config.likelihoodCriteria)?.label ?? String(riskAssessment.likelihood)) : String(riskAssessment.likelihood)}
              config={config}
              onSave={async (newLikelihood) => {
                const currentSeverity = riskAssessment.severity;
                const computed = config ? scoreToRiskLevel(newLikelihood, currentSeverity, config) : null;
                // riskLevel은 등급 인덱스(1~N, N≤5) — 서버 DTO 1~10 제약 준수
                const newRiskLevel = computed?.riskLevel ?? 1;
                await onSave({
                  likelihood: newLikelihood,
                  riskLevel: newRiskLevel,
                });
              }}
            />
          </ModifiedFieldHighlight>
          <ModifiedFieldHighlight
            modification={findModification(aiModifications?.modifiedFields, 'riskAssessment.severity')}
            editable={!!canEdit}
            formatOriginal={makeOriginalFormatter(config?.severityCriteria)}
            originalPosition="tooltip"
            onRevert={onRevert ? async () => {
              const mod = findModification(aiModifications?.modifiedFields, 'riskAssessment.severity');
              if (mod) {
                const originalObj = mod.originalValue as { value: number; label?: string } | number;
                const original = typeof originalObj === 'number' ? originalObj : originalObj.value;
                await onRevert('severity', original);
              }
            } : undefined}
          >
            <EditableRiskBadge
              type="severity"
              value={riskAssessment.severity}
              label={config ? (normalizedToLevel(riskAssessment.severity, config.severityCriteria)?.label ?? String(riskAssessment.severity)) : String(riskAssessment.severity)}
              config={config}
              onSave={async (newSeverity) => {
                const currentLikelihood = riskAssessment.likelihood;
                const computed = config ? scoreToRiskLevel(currentLikelihood, newSeverity, config) : null;
                // riskLevel은 등급 인덱스(1~N, N≤5) — 서버 DTO 1~10 제약 준수
                const newRiskLevel = computed?.riskLevel ?? 1;
                await onSave({
                  severity: newSeverity,
                  riskLevel: newRiskLevel,
                });
              }}
            />
          </ModifiedFieldHighlight>
        </>
      ) : (
        <>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded border text-xs">
            <span className="text-ink-500">빈도:</span>
            <span className="font-medium">{config ? (normalizedToLevel(riskAssessment.likelihood, config.likelihoodCriteria)?.label ?? String(riskAssessment.likelihood)) : String(riskAssessment.likelihood)}</span>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded border text-xs">
            <span className="text-ink-500">강도:</span>
            <span className="font-medium">{config ? (normalizedToLevel(riskAssessment.severity, config.severityCriteria)?.label ?? String(riskAssessment.severity)) : String(riskAssessment.severity)}</span>
          </span>
        </>
      )}
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-medium text-xs ${getRiskStyle(computedRiskLevel, computedColor)}`}>
        <span>위험도:</span>
        <span>{computedRiskLabel}</span>
      </span>
    </>
  );
}

export default RiskAssessmentEditor;
